/**
 * Business profile — the five onboarding answers that decide how much of the
 * product a given account sees.
 *
 * Nothing is ever deleted by a profile: every effect is a hide, and every answer
 * is reversible from the Account page. An account with no saved profile keeps the
 * full surface, so existing users are untouched.
 */
import { useCallback, useEffect, useState } from "react";

import { safeGetItem, safeSetItem, safeRemoveItem } from "@/lib/safe-storage";

export const PROFILE_KEY = "mypriorities:business-profile";
export const PROFILE_EVENT = "mypriorities:business-profile-change";

export type RevenueModel = "oneoff" | "ongoing" | "both";
export type TeamSize = "solo" | "few" | "team";
export type StageStyle = "process" | "sort-of" | "none";
export type ContractTerms = "both" | "break" | "uplift" | "neither";

export type BusinessProfile = {
  revenue: RevenueModel;
  team: TeamSize;
  stages: StageStyle;
  /** Key of the chosen stage template, or "custom". */
  stageTemplate?: string;
  /** Free-typed stage names when `stageTemplate` is "custom". */
  customStages?: string[];
  contractTerms?: ContractTerms;
};

export type FeatureSet = {
  /** Renewals, QBRs, sales calendar and the revenue plan. */
  renewals: boolean;
  /** RPI / CPI uplift fields on contracts. */
  uplifts: boolean;
  /** Break clause, notice period and auto-renewal fields. */
  breakClauses: boolean;
  /** Staged pipeline and forecast views. */
  pipeline: boolean;
  /** Team, allocation and shared ownership surfaces. */
  teamViews: boolean;
  /** Granular org permissions in Sales settings. */
  permissions: boolean;
  /** What long-form commitments are called for this account. */
  contractsLabel: string;
};

const FULL: FeatureSet = {
  renewals: true,
  uplifts: true,
  breakClauses: true,
  pipeline: true,
  teamViews: true,
  permissions: true,
  contractsLabel: "Contracts",
};

/** Everything is on unless the account has answered otherwise. */
export function deriveFeatures(profile: BusinessProfile | null | undefined): FeatureSet {
  if (!profile) return FULL;
  const ongoing = profile.revenue !== "oneoff";
  const terms = profile.contractTerms ?? "both";
  return {
    renewals: ongoing,
    uplifts: ongoing && (terms === "both" || terms === "uplift"),
    breakClauses: ongoing && (terms === "both" || terms === "break"),
    pipeline: profile.stages !== "none",
    teamViews: profile.team !== "solo",
    permissions: profile.team === "team",
    contractsLabel: ongoing ? "Contracts" : "Projects",
  };
}

/** Menu entries a profile switches off. Users can still turn them back on. */
export function hiddenNavIds(features: FeatureSet): string[] {
  const hidden: string[] = [];
  if (!features.renewals) {
    hidden.push("customers:renewals", "revenue:plan", "revenue:calendar");
  }
  if (!features.pipeline) {
    hidden.push("revenue:pipeline", "revenue:forecast");
  }
  if (!features.teamViews) {
    hidden.push("revenue:team", "tasks:inbox", "customers:inbox");
  }
  return hidden;
}

export const STAGE_TEMPLATES: {
  key: string;
  label: string;
  description: string;
  stages: { name: string; probability: number; won?: boolean; lost?: boolean }[];
}[] = [
  {
    key: "simple",
    label: "Simple (3 stages)",
    description: "Enquiry, quoted, won.",
    stages: [
      { name: "Enquiry", probability: 20 },
      { name: "Quoted", probability: 60 },
      { name: "Won", probability: 100, won: true },
      { name: "Lost", probability: 0, lost: true },
    ],
  },
  {
    key: "standard",
    label: "Standard (5 stages)",
    description: "Qualify, propose, negotiate, closed.",
    stages: [
      { name: "Qualifying", probability: 10 },
      { name: "Discovery", probability: 30 },
      { name: "Proposal", probability: 55 },
      { name: "Negotiation", probability: 80 },
      { name: "Won", probability: 100, won: true },
      { name: "Lost", probability: 0, lost: true },
    ],
  },
  {
    key: "enterprise",
    label: "Enterprise (7 stages)",
    description: "Full procurement path with legal and sign-off.",
    stages: [
      { name: "Lead", probability: 5 },
      { name: "Qualified", probability: 15 },
      { name: "Discovery", probability: 30 },
      { name: "Solution", probability: 45 },
      { name: "Proposal", probability: 60 },
      { name: "Legal & procurement", probability: 80 },
      { name: "Verbal agreement", probability: 90 },
      { name: "Won", probability: 100, won: true },
      { name: "Lost", probability: 0, lost: true },
    ],
  },
];

function isProfile(value: unknown): value is BusinessProfile {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    (v["revenue"] === "oneoff" || v["revenue"] === "ongoing" || v["revenue"] === "both") &&
    (v["team"] === "solo" || v["team"] === "few" || v["team"] === "team") &&
    (v["stages"] === "process" || v["stages"] === "sort-of" || v["stages"] === "none")
  );

}

/** Read the saved profile from this browser. Returns null when unanswered. */
export function readProfile(): BusinessProfile | null {
  const raw = safeGetItem(PROFILE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return isProfile(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function notifyProfileChange() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PROFILE_EVENT));
}

/** Write the profile locally (remote sync lives in prefs-sync). */
export function writeProfileLocal(profile: BusinessProfile | null) {
  if (profile) safeSetItem(PROFILE_KEY, JSON.stringify(profile));
  else safeRemoveItem(PROFILE_KEY);
  notifyProfileChange();
}

/** The saved profile, read after hydration; `undefined` while unknown. */
export function useBusinessProfile() {
  const [profile, setProfile] = useState<BusinessProfile | null | undefined>(undefined);

  useEffect(() => {
    const sync = () => setProfile(readProfile());
    sync();
    window.addEventListener(PROFILE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PROFILE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const save = useCallback((next: BusinessProfile | null) => {
    writeProfileLocal(next);
  }, []);

  return { profile, saveProfile: save, features: deriveFeatures(profile ?? null) };
}

/** Feature set for the current account — permissive until the profile loads. */
export function useFeatures(): FeatureSet {
  const { features } = useBusinessProfile();
  return features;
}
